// Admin counterpart of web/src/RepairLogModal.tsx — same UX, but
// targets the admin endpoint so admins can log on a customer's
// vehicle without ownership constraints. Keep in sync structurally
// with the web copy; only the POST URL differs.

import { useState } from 'react';
import { api, RepairEvent, RepairKind, REPAIR_KINDS, REPAIR_KIND_LABELS } from './api';

interface Props {
  vehicleId: number;
  vehicleName?: string;
  initialCylinder?: number | null;
  initialKind?: RepairKind;
  numCylinders: number;
  onClose: () => void;
  onSaved: (event: RepairEvent) => void;
}

function toLocalDatetimeInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function parseLocalDatetimeInput(s: string): number | null {
  if (!s) return null;
  const ms = new Date(s).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function RepairLogModal({
  vehicleId, vehicleName, initialCylinder, initialKind, numCylinders,
  onClose, onSaved,
}: Props) {
  const [cylinderId, setCylinderId] = useState<number | ''>(
    initialCylinder ?? '',
  );
  const [repairKind, setRepairKind] = useState<RepairKind>(
    initialKind ?? 'spark_plug',
  );
  const [occurredAtStr, setOccurredAtStr] = useState<string>(
    toLocalDatetimeInput(Date.now()),
  );
  const [notes, setNotes] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    const occurredAt = parseLocalDatetimeInput(occurredAtStr);
    if (occurredAt == null) {
      setError('Pick a valid date / time');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await api.post<RepairEvent>(
        `/api/admin/vehicles/${vehicleId}/repair-events`,
        {
          cylinder_id: cylinderId === '' ? null : Number(cylinderId),
          repair_kind: repairKind,
          notes: notes.trim() === '' ? null : notes.trim(),
          occurred_at: occurredAt,
        },
      );
      onSaved(saved);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 520, width: '95%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>
            Log repair{vehicleName ? ` — ${vehicleName}` : ''}
          </h3>
          <button className="modal-close" onClick={onClose} aria-label="close">×</button>
        </div>

        <div className="muted" style={{ fontSize: '0.85rem', marginBottom: 8 }}>
          Records what was done so the trends modal can mark when it
          happened and compute before-vs-after metric changes. Logged
          via the admin endpoint — the resulting row is owned by the
          vehicle's owner with recorded_by='admin'.
        </div>

        <div className="field">
          <label>Cylinder</label>
          <select
            value={cylinderId}
            onChange={(e) => setCylinderId(
              e.target.value === '' ? '' : Number(e.target.value),
            )}
            disabled={busy}
            style={{ width: '100%' }}
          >
            <option value="">Whole engine (no specific cyl)</option>
            {Array.from({ length: numCylinders }, (_, i) => i + 1).map((c) => (
              <option key={c} value={c}>Cyl {c}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Repair kind</label>
          <select
            value={repairKind}
            onChange={(e) => setRepairKind(e.target.value as RepairKind)}
            disabled={busy}
            style={{ width: '100%' }}
          >
            {REPAIR_KINDS.map((k) => (
              <option key={k} value={k}>{REPAIR_KIND_LABELS[k]}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>When did this happen?</label>
          <input
            type="datetime-local"
            value={occurredAtStr}
            onChange={(e) => setOccurredAtStr(e.target.value)}
            disabled={busy}
            style={{ width: '100%' }}
          />
        </div>

        <div className="field">
          <label>Notes (optional)</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. NGK Iridium, gapped 0.044"
            disabled={busy}
            maxLength={500}
            style={{ width: '100%' }}
          />
        </div>

        {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}

        <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
          <button className="secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save repair'}
          </button>
        </div>
      </div>
    </div>
  );
}
